import React from "react";
import { LegendPayload } from "recharts";

export type LegendPosition = "top" | "bottom";

export interface CustomLegendRendererOptions {
  position?: LegendPosition;
  wrapperStyles?: React.CSSProperties;
  itemStyles?: React.CSSProperties;
  iconRenderer?: (entry: LegendPayload, index: number) => React.ReactNode;
}

// Default icon renderer - circular icons
const defaultIconRenderer = (entry: LegendPayload, index: number): React.ReactNode => {
  return (
    <span
      style={{
        display: "inline-block",
        width: "12px",
        height: "12px",
        borderRadius: "50%",
        backgroundColor: entry.color,
      }}
    />
  );
};

export const createCustomLegendRenderer = (options?: CustomLegendRendererOptions) => {
  return (props: any): React.ReactNode => {
    const { payload } = props;
    if (!payload) return null;

    const position = options?.position || "bottom";
    const isTop = position === "top";

    const defaultWrapperStyles: React.CSSProperties = {
      display: "flex",
      justifyContent: "center",
      flexDirection: "row-reverse",
      gap: "24px",
      listStyle: "none",
      margin: 0,
      padding: 0,
      fontFamily: "ABC Whyte",
    };

    const defaultItemStyles: React.CSSProperties = {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontFamily: "ABC Whyte",
    };

    // Adjust padding based on position
    const positionStyles: React.CSSProperties = isTop
      ? { paddingBottom: "20px" }
      : { paddingTop: "20px" };

    const wrapperStyle = { 
      ...defaultWrapperStyles, 
      ...positionStyles,
      ...options?.wrapperStyles 
    };
    const itemStyle = { ...defaultItemStyles, ...options?.itemStyles };
    const iconRenderer = options?.iconRenderer || defaultIconRenderer;

    return (
      <ul style={wrapperStyle}>
        {payload.map((entry: LegendPayload, index: number) => (
          <li 
            key={`legend-${index}`} 
            style={itemStyle}
          >
            {iconRenderer(entry, index)}
            <span style={{ color: entry.color, fontFamily: "ABC Whyte" }}>
              {entry.value}
            </span>
          </li>
        ))}
      </ul>
    );
  };
};

// Default export for backward compatibility
export const customLegendRenderer = createCustomLegendRenderer();
